import { useEffect } from 'react';
import { MAX_RECOVERY_ATTEMPTS, recoveryAttemptsUsed, recordRecoveryAttempt } from 'Hooks/loadingRecoveryBudget';

/**
 * Full-screen loading recovery watchdog.
 *
 * When the app sits on the full-screen loading spinner for too long - typically because a
 * backend is cold-starting and a bootstrap request is hanging with the connection held open -
 * there is otherwise no way to recover in an unattended context (e.g. the VisiCamRecorder
 * Electron kiosk, which has no user to refresh and no devtools). After RELOAD_AFTER_MS on
 * screen this reloads the page, which re-fires the bootstrap against a by-then-warm backend.
 *
 * Automatic reloads are capped via the shared loadingRecoveryBudget so a genuinely
 * unreachable backend can't cause an endless reload loop; the cap resets after a staleness
 * window. Skipped under Storybook. (NN-3133)
 */
export const RELOAD_AFTER_MS = 75000;

const useStuckLoadingReload = () => {
    useEffect(() => {
        // Don't auto-reload the Storybook iframe when a spinner story is left open.
        if (process.env.STORYBOOK) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            if (recoveryAttemptsUsed() >= MAX_RECOVERY_ATTEMPTS) {
                return;
            }

            recordRecoveryAttempt();
            window.location.reload();
        }, RELOAD_AFTER_MS);

        return () => clearTimeout(timeoutId);
    }, []);
};

export default useStuckLoadingReload;
